import React, { useEffect, useState } from 'react';
import { BunkerFormHeader, BunkerRegColumnWrapper, BunkerRegFormWrapper, BunkerRegInputSectionsWrapper } from '../Bunkering.styles';
import { BunkerRegSectionType, validateTotalReceivedVsAllocatedFuel } from '../BunkeringUtils';
import { useBunkerContext } from 'src/context/BunkerContext';
import { InputType } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import DebunkeringAssignment from './debunkering/DebunkeringAssignment';
import { BunkeringEvent, BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkerReceived } from 'src/types/ViewModel/Bunkering/BunkerReceived';
import { UUID } from 'crypto';
import BunkerRegTabsContainer from './BunkerRegTabsContainer';
import BunkerRegistrationSection from './BunkerRegistrationSection';
import { WarningText } from 'src/components/shared/warning-text/WarningText.style';

interface BunkerRegistrationProps {
    onSaveSelectedBunkering: () => void;
    handleInputWithChangeObject: (changeObject: any, inputType: InputType) => void;
    updateBunkerReceivedPerTanks: (bunkerReceivedPerTanks: BunkerReceived[]) => void;
    removeDebunkering: (debunkeringEvent: BunkeringEvent) => void;
}

const BunkerRegistration: React.FC<BunkerRegistrationProps> = ({ handleInputWithChangeObject, updateBunkerReceivedPerTanks, removeDebunkering }) => {
    const { bunkeringEvents, selectedBunkering, selectedBunkeringEvent } = useBunkerContext();

    const [debunkeringEvents, setDebunkeringEvents] = useState<BunkeringEvent[]>(null);
    const [fuelAllocationValid, setFuelAllocationValid] = useState<boolean>(true);

    useEffect(() => {
        if (selectedBunkering && selectedBunkering.debunkeringEventIds && selectedBunkering.debunkeringEventIds.length > 0) {
            const _debunkeringEvents = bunkeringEvents.filter((e) => selectedBunkering.debunkeringEventIds.includes(e.eventID as UUID));
            setDebunkeringEvents(_debunkeringEvents);
        } else {
            setDebunkeringEvents(null);
        }

        setFuelAllocationValid(validateTotalReceivedVsAllocatedFuel(selectedBunkering));
    }, [selectedBunkering]);

    if (selectedBunkeringEvent && !selectedBunkeringEvent.debunkering) {
        return (
            <BunkerRegFormWrapper>
                <BunkerFormHeader>
                    <h2>{selectedBunkering.bunkeringReference}</h2>
                </BunkerFormHeader>
                <BunkerRegInputSectionsWrapper>
                    <BunkerRegColumnWrapper>
                        <BunkerRegistrationSection
                            title="Operational Data"
                            section={BunkerRegSectionType.OperationalData}
                            selectedBunkeringEvent={selectedBunkeringEvent}
                            selectedBunkering={selectedBunkering}
                            debunkeringEvents={debunkeringEvents}
                            handleInputWithChangeObject={handleInputWithChangeObject}
                            removeDebunkering={removeDebunkering}
                        />
                        <BunkerRegistrationSection
                            title="Bunker Delivery Note"
                            section={BunkerRegSectionType.BDN}
                            selectedBunkeringEvent={selectedBunkeringEvent}
                            selectedBunkering={selectedBunkering}
                            debunkeringEvents={debunkeringEvents}
                            handleInputWithChangeObject={handleInputWithChangeObject}
                            removeDebunkering={removeDebunkering}
                        />
                        {!fuelAllocationValid && (
                            <WarningText style={{ marginTop: '0' }}>
                                Fuel allocation to tanks does not match total quantity received.
                                <br />
                                Total received quantity: {selectedBunkering.totalReceivedQuantity?.toFixed(2)} MT
                                <br />
                                Total allocated to tanks:{' '}
                                {selectedBunkering.bunkerReceivedPerTanks.reduce((acc, curr) => acc + curr.bunkerReceivedMt, 0).toFixed(2)} MT
                            </WarningText>
                        )}
                        {!selectedBunkering?.bunkeringStatuses?.some((status) => status?.status === BunkeringStatusEnum.Approved) &&
                            selectedBunkering?.currentStatus?.status === BunkeringStatusEnum.InUse && (
                                <WarningText style={{ marginTop: '0' }}>
                                    Fuel is in use, but not approved. Please approve as soon as documentation received.
                                </WarningText>
                            )}
                    </BunkerRegColumnWrapper>
                    <BunkerRegColumnWrapper>
                        <BunkerRegistrationSection
                            title="Fuel Test Analysis Results"
                            section={BunkerRegSectionType.FTA}
                            selectedBunkeringEvent={selectedBunkeringEvent}
                            selectedBunkering={selectedBunkering}
                            debunkeringEvents={debunkeringEvents}
                            handleInputWithChangeObject={handleInputWithChangeObject}
                            removeDebunkering={removeDebunkering}
                        />
                        {selectedBunkeringEvent.fuelType === FuelType.BIO && (
                            <BunkerRegistrationSection
                                title="Proof of Sustainability"
                                section={BunkerRegSectionType.ProofOfSustainability}
                                selectedBunkeringEvent={selectedBunkeringEvent}
                                selectedBunkering={selectedBunkering}
                                debunkeringEvents={debunkeringEvents}
                                handleInputWithChangeObject={handleInputWithChangeObject}
                                removeDebunkering={removeDebunkering}
                            />
                        )}
                    </BunkerRegColumnWrapper>
                </BunkerRegInputSectionsWrapper>

                <BunkerRegTabsContainer updateBunkerReceivedPerTanks={updateBunkerReceivedPerTanks} />
            </BunkerRegFormWrapper>
        );
    } else if (selectedBunkeringEvent && selectedBunkeringEvent.debunkering) {
        return (
            <BunkerRegFormWrapper>
                <BunkerFormHeader>
                    <h2>{`Debunkering: ${FuelType[selectedBunkeringEvent.fuelType]}`}</h2>
                </BunkerFormHeader>
                <DebunkeringAssignment debunkeringEvent={selectedBunkeringEvent} />
            </BunkerRegFormWrapper>
        );
    } else return <BunkerRegFormWrapper />;
};

export default BunkerRegistration;
